/**
 * NodeRow — one row of the list-builder's indented tree.
 *
 * Responsibilities:
 *   - Draws indentation rails + connector hook per depth.
 *   - Shows the parent-edge's branch-key label (or "ROOT"/"orphan").
 *   - Renders the node's type badge, ref number, name, config summary,
 *     and recording-slot chips (all 4 slots, filled=clickable, empty=muted).
 *   - Hover-revealed action cluster: [+] add child, pencil edit, × delete.
 *   - Collapse/expand chevron (if the node has children).
 *
 * Zero data-fetching lives here — the row is a pure component; the
 * surrounding <TreeListView /> passes everything in via props.
 */
import { forwardRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { IvrNodeRow } from '@/api/ivrNodes';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { isMenuLike } from './AddChildPopover';
import { describeBranchKey } from './styles/common';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { recordings } from '@/api/recordings';
import { recordingFolders } from '@/api/recordingFolders';
import { voicemails } from '@/api/voicemails';
import { useAuth } from '@/lib/auth-store';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ChevronLeft, ChevronRight, Plus, Pencil, X,
  Play, Lock, FolderOpen, Voicemail, ExternalLink, Trash2, Mail, MailOpen, Download,
  CheckCircle2, XCircle,
  PhoneOff, ArrowRight, AlertTriangle,
} from 'lucide-react';

// ── Type accent palette ───────────────────────────────────────────────────────

interface TypeStyle { rail: string; bg: string; text: string; border: string }

const TYPE_STYLES: Record<string, TypeStyle> = {
  menu:               { rail: 'bg-indigo-400',  bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200' },
  playback:           { rail: 'bg-sky-400',     bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200' },
  bulletin:           { rail: 'bg-sky-400',     bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200' },
  series_folder:      { rail: 'bg-cyan-400',    bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200' },
  latest_n_folder:    { rail: 'bg-cyan-400',    bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200' },
  rotation_folder:    { rail: 'bg-cyan-400',    bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200' },
  random_folder:      { rail: 'bg-cyan-400',    bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-200' },
  voicemail:          { rail: 'bg-amber-400',   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  listen_voicemail:   { rail: 'bg-amber-400',   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  conference:         { rail: 'bg-violet-400',  bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  payment:            { rail: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  shopping:           { rail: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  quiz:               { rail: 'bg-fuchsia-400', bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  survey:             { rail: 'bg-fuchsia-400', bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
  transfer:           { rail: 'bg-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  dial:               { rail: 'bg-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  ring_group:         { rail: 'bg-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  sip_connect:        { rail: 'bg-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  extension:          { rail: 'bg-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  jump:               { rail: 'bg-slate-400',   bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
  back:               { rail: 'bg-slate-400',   bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
  repeat:             { rail: 'bg-slate-400',   bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
  return_to_bookmark: { rail: 'bg-teal-400',    bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200' },
  bookmark:           { rail: 'bg-teal-400',    bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200' },
};

function styleForType(t: string): TypeStyle {
  return TYPE_STYLES[t] ?? {
    rail: 'bg-slate-300',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200',
  };
}

// ── Branch-key pill ───────────────────────────────────────────────────────────

function BranchKeyPill({ k, isRoot, isOrphan }: {
  k: string | null;
  isRoot: boolean;
  isOrphan: boolean;
}) {
  if (isRoot) {
    return (
      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
        ROOT
      </span>
    );
  }
  if (isOrphan || !k) {
    return (
      <span className="shrink-0 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-amber-600">
        orphan
      </span>
    );
  }
  const special = ['timeout', 'fallback', 'invalid', 'default'].includes(k);
  const next = k === 'next';
  return (
    <span
      title={describeBranchKey(k)}
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded border px-1.5 py-0.5 font-mono text-[11px] font-semibold leading-none min-w-[28px]',
        special
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : next
            ? 'bg-slate-50 text-slate-600 border-slate-200'
            : 'bg-indigo-50 text-indigo-700 border-indigo-200',
      )}
    >
      {k}
    </span>
  );
}

// ── Config summary per node type ──────────────────────────────────────────────
// Takes the existing node + its outgoing edges and returns a short
// human-readable line. Order of fields deliberately small so the row
// stays scannable.

export function summariseNode(
  node: IvrNodeRow,
  outgoingEdges: ReadonlyArray<IvrEdgeRow>,
): string | null {
  const cfg = (node.config ?? {}) as Record<string, unknown>;
  switch (node.node_type) {
    case 'menu':
    case 'playback_settings': {
      const digitKeys = outgoingEdges.filter((e) => /^[0-9*#]$/.test(e.branch_key));
      const parts: string[] = [];
      parts.push(`${digitKeys.length} option${digitKeys.length === 1 ? '' : 's'}`);
      const timeoutSec = typeof cfg.timeout_seconds === 'number' ? cfg.timeout_seconds : undefined;
      if (typeof timeoutSec === 'number') parts.push(`${timeoutSec}s timeout`);
      return parts.join(' \u00b7 ');
    }
    case 'playback':
      return node.main_recording_id ? 'recording set' : 'no recording';
    // voicemail + listen_voicemail render via VoicemailBoxLabel below —
    // they point at a voicemail BOX (cfg.voicemail_box_id), not a
    // recording folder. Returning null here lets the row component
    // render the box label inline instead of a generic "folder" string.
    case 'voicemail':
    case 'listen_voicemail':
      return null;
    // Recording-folder-bound nodes — handled by FolderLinkLabel for a
    // clickable label that shows the actual folder NAME (and navigates
    // to /folders/:id on click). Returning null here suppresses the
    // generic "folder linked" plain text. The renderer checks
    // getRecordingFolderId() first; if non-null, FolderLinkLabel wins.
    case 'series_folder':
    case 'sequential_folder':
    case 'unlock_series_folder':
    case 'unlock_sequential_folder':
    case 'bulletin':
    case 'latest_n_folder':
    case 'rotation_folder':
    case 'random_folder':
      return null;
    case 'conference': {
      const room = cfg.conference_room_id;
      return room ? 'room linked' : 'no room';
    }
    case 'transfer':
    case 'dial':
    case 'sip_connect':
    case 'extension':
    case 'ring_group': {
      const target = cfg.target_tree_id ?? cfg.did ?? cfg.sip_uri ?? cfg.extension_id ?? cfg.ring_group_id;
      return target ? 'target set' : 'no target';
    }
    case 'jump':
    case 'return_to_bookmark':
      return node.fallback_node_id ? 'target set' : 'no target';
    case 'payment': {
      const amount = cfg.amount_cents ?? cfg.amount;
      if (typeof amount === 'number') {
        return `$${(amount / 100).toFixed(2)}`;
      }
      return cfg.shopping_cart_node_id ? 'cart-driven' : 'no amount';
    }
    case 'quiz':
    case 'survey': {
      const count = Array.isArray(cfg.questions) ? cfg.questions.length : undefined;
      return typeof count === 'number' ? `${count} question${count === 1 ? '' : 's'}` : null;
    }
    case 'data_collection': {
      const field = cfg.field ?? cfg.field_name;
      return field ? `field: ${field}` : null;
    }
    default:
      return null;
  }
}

// ── Recording-folder link label ──────────────────────────────────────────────
//
// Folder-bound node types (series_folder, sequential_folder, unlock_*,
// bulletin, latest_n_folder, rotation_folder, random_folder) get a clickable
// label showing the folder's NAME (instead of a generic "folder linked"
// string). Click navigates to /folders/<id>.
//
// We resolve the id from `config.folder_id` (canonical) with a fallback to
// the legacy `recording_folder_id` field that some older tree-builder
// presets used.

const RECORDING_FOLDER_NODE_TYPES = new Set<string>([
  'series_folder',
  'sequential_folder',
  'unlock_series_folder',
  'unlock_sequential_folder',
  'bulletin',
  'latest_n_folder',
  'rotation_folder',
  'random_folder',
]);

/** Returns the linked recording-folder id for a folder-bearing node, or null. */
export function getRecordingFolderId(node: IvrNodeRow): string | null {
  if (!RECORDING_FOLDER_NODE_TYPES.has(node.node_type)) return null;
  const cfg = (node.config ?? {}) as Record<string, unknown>;
  const id = cfg.folder_id ?? cfg.recording_folder_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * Inline label rendered in place of the generic "folder linked" string.
 *
 *   - `null` folderId      → "no folder" (muted, not clickable)
 *   - has folderId, loading → "folder…" placeholder
 *   - has folderId, error   → "folder unavailable" (amber, hover for tooltip)
 *   - has folderId, loaded  → "📁 <name>" clickable link to /folders/<id>
 *
 * Uses React Query's per-id cache so opening the tree N times only fetches
 * each unique folder once. Stale-time is generous (5 min) since folder names
 * change rarely and the list view doesn't need to be perfectly live.
 */
export function FolderLinkLabel({ folderId }: { folderId: string | null }) {
  const enabled = Boolean(folderId);
  const q = useQuery({
    queryKey: ['recording-folder', folderId],
    queryFn: () => recordingFolders.get(folderId!),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!folderId) {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-slate-400 italic"
        title="No folder linked yet — open the node editor to assign one"
      >
        <FolderOpen className="h-3 w-3" aria-hidden />
        no folder
      </span>
    );
  }

  if (q.isPending) {
    return (
      <span className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-slate-400">
        <FolderOpen className="h-3 w-3 animate-pulse" aria-hidden />
        folder…
      </span>
    );
  }

  if (q.isError || !q.data) {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-amber-600"
        title={`Folder ${folderId} could not be loaded — it may have been deleted`}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden />
        folder unavailable
      </span>
    );
  }

  const folder = q.data;
  return <FolderLinkButton folder={folder} />;
}

/**
 * Folder link rendered as a button that opens a quick-edit Dialog instead
 * of navigating away. Per Sam's request: "open a popup window to the
 * folder instead of going to the folder page so can edit folder but stay
 * here". The dialog has name + description editable inline + an
 * "Open full page" link as a fallback for items management + reordering.
 */
function FolderLinkButton({ folder }: {
  folder: Awaited<ReturnType<typeof recordingFolders.get>>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="hidden md:inline-flex items-center gap-1 max-w-[180px] text-[11.5px] text-indigo-600 hover:text-indigo-700 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400/40 rounded"
        title={`Quick-edit folder ${formatRef(folder.ref_number)} — ${folder.name}`}
      >
        <FolderOpen className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{folder.name}</span>
      </button>
      {open && (
        <FolderQuickEditDialog folder={folder} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/**
 * Folder contents popup — overlays the IVR tree without navigation. Shows
 * the list of recordings inside the folder with their position/sort
 * order, durations, an inline audio player, reorder up/down buttons, and
 * remove. Big modal (max-w-3xl) so it covers a "big part" of the page
 * but doesn't obscure the row context entirely.
 *
 * Items management (add/remove/reorder) is in scope; folder-level
 * settings (rename, change folder_type, etc.) stay on the full page —
 * link at the bottom jumps there.
 *
 * Pagination: client-side slice over the full listItems response (the
 * API doesn't paginate). Mirrors AuthListDetailPage's footer:
 * 50/100/200/ALL, default 50. Reorder still works in paginated mode
 * — the up/down buttons operate on the global item index, not the
 * page-local one, so item #51 can move to #50 (crosses page boundary).
 */
const FOLDER_PAGE_SIZES = [50, 100, 200, 'ALL'] as const;
type FolderPageSize = typeof FOLDER_PAGE_SIZES[number];

function FolderQuickEditDialog({
  folder, onClose,
}: {
  folder: Awaited<ReturnType<typeof recordingFolders.get>>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const accessToken = useAuth((s) => s.accessToken);

  const itemsQuery = useQuery({
    queryKey: ['folderItems', folder.id],
    queryFn: () => recordingFolders.listItems(folder.id),
  });
  const rawItems = itemsQuery.data?.rows ?? [];
  // Optimistic order — same idiom as FolderDetail.
  const [localOrder, setLocalOrder] = useState<typeof rawItems | null>(null);
  const items = localOrder ?? [...rawItems].sort((a, b) => a.sort_order - b.sort_order);

  const [openSlot, setOpenSlot] = useState<string | null>(null);

  // ── Pagination (client-side; API returns the full list) ─────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<FolderPageSize>(50);
  const pageCount = pageSize === 'ALL'
    ? 1
    : Math.max(1, Math.ceil(items.length / pageSize));
  const startIdx = pageSize === 'ALL' ? 0 : page * pageSize;
  const endIdx = pageSize === 'ALL' ? items.length : Math.min(startIdx + pageSize, items.length);
  const visibleItems = items.slice(startIdx, endIdx);
  const showingFrom = items.length === 0 ? 0 : startIdx + 1;
  const showingTo = endIdx;
  // Clamp page if items shrink (e.g. after a remove on the last page) —
  // useEffect not inline-conditional setPage to avoid render-phase
  // state updates that React 18 will warn on.
  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const remove = useMutation({
    mutationFn: (itemId: string) => recordingFolders.removeItem(itemId),
    onSuccess: () => {
      setLocalOrder(null);
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
      qc.invalidateQueries({ queryKey: ['recording-folder', folder.id] });
    },
  });

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => recordingFolders.reorderItems(folder.id, itemIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
    },
    onError: () => setLocalOrder(null),
  });

  // moveItem operates on the GLOBAL index so reorder works across page
  // boundaries. The visible row passes its own globalIdx down via the
  // closure below.
  function moveItem(fromIndex: number, toIndex: number): void {
    if (toIndex < 0 || toIndex >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(fromIndex, 1);
    if (!moved) return;
    next.splice(toIndex, 0, moved);
    setLocalOrder(next);
    reorder.mutate(next.map((i) => i.id));
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FolderOpen className="h-4 w-4 text-indigo-600" />
            <span className="font-mono text-xs text-slate-400">{formatRef(folder.ref_number)}</span>
            <span>{folder.name}</span>
            <span className="text-[10px] uppercase tracking-wider rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">
              {folder.folder_type}
            </span>
            <span className="text-xs text-muted-foreground font-normal">
              · {items.length} recording{items.length === 1 ? '' : 's'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Recordings list — frame around the list mirrors the voicemail
            popup's frame (rounded-md + border-border + overflow-hidden).
            Same visual treatment so both quick-view dialogs read alike. */}
        <div className="flex-1 overflow-auto -mx-6 px-6 py-2">
          {itemsQuery.isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center italic">
              No recordings in this folder yet. Use the full-page editor
              (link below) to add some.
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
              {visibleItems.map((item, visibleIdx) => {
                const globalIdx = startIdx + visibleIdx;
                return (
                <li key={item.id} className="px-1 py-2">
                  <div className="flex items-center gap-2">
                    {/* Up/down — operates on globalIdx so reorder still
                        works correctly when paginated. */}
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => moveItem(globalIdx, globalIdx - 1)}
                        disabled={globalIdx === 0 || reorder.isPending}
                        className="h-3.5 w-5 inline-flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        aria-label="Move up"
                        title="Move up"
                      >
                        <ChevronRight className="h-3 w-3 -rotate-90" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(globalIdx, globalIdx + 1)}
                        disabled={globalIdx === items.length - 1 || reorder.isPending}
                        className="h-3.5 w-5 inline-flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        aria-label="Move down"
                        title="Move down"
                      >
                        <ChevronRight className="h-3 w-3 rotate-90" />
                      </button>
                    </div>

                    {/* Position number — always the global index, so the
                        user sees real positions across pages. */}
                    <span className="font-mono text-xs text-muted-foreground tabular-nums w-8 text-right shrink-0">
                      {globalIdx + 1}
                    </span>

                    {/* Play toggle + name */}
                    <button
                      type="button"
                      onClick={() => setOpenSlot(openSlot === item.id ? null : item.id)}
                      className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      aria-label={openSlot === item.id ? 'Close player' : 'Play preview'}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </button>
                    <span className="truncate text-sm font-medium flex-1 min-w-0" title={item.recording_name || '(untitled)'}>
                      {item.recording_name || '(untitled)'}
                    </span>

                    {/* Inline player when open */}
                    {openSlot === item.id && (
                      <div className="w-72 shrink-0">
                        <AudioPlayer
                          src={recordings.audioUrl(item.recording_id, accessToken)}
                          compact
                        />
                      </div>
                    )}

                    {/* Duration */}
                    <span className="text-xs text-muted-foreground tabular-nums w-14 text-right shrink-0">
                      {item.recording_duration_ms
                        ? `${Math.floor(item.recording_duration_ms / 60000)}:${String(Math.floor((item.recording_duration_ms / 1000) % 60)).padStart(2, '0')}`
                        : '—'}
                    </span>

                    {/* Remove */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(item.id)}
                      disabled={remove.isPending}
                      className="h-7 w-7 p-0 shrink-0 text-slate-400 hover:text-red-600"
                      aria-label="Remove from folder"
                      title="Remove from folder"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination footer — same shape as AuthListDetailPage. */}
        {items.length > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-xs pt-2 border-t border-border">
            <div className="text-muted-foreground">
              Showing {showingFrom}–{showingTo} of {items.length}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const v = e.target.value;
                  setPageSize(v === 'ALL' ? 'ALL' : (Number(v) as FolderPageSize));
                  setPage(0);
                }}
                className="rounded border border-input bg-background px-2 py-1 text-xs"
              >
                {FOLDER_PAGE_SIZES.map((n) => (
                  <option key={String(n)} value={String(n)}>{n}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0 || pageSize === 'ALL'}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="font-mono">
                Page {page + 1} of {pageCount}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page + 1 >= pageCount || pageSize === 'ALL'}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground">
            Reorder by ↑/↓ buttons. Add new recordings on the full page.
          </p>
          <Link
            to={`/folders/FLD-${folder.ref_number}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
          >
            <ExternalLink className="h-3 w-3" />
            Open full page (add recordings, edit settings)
          </Link>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Voicemail-box link label ────────────────────────────────────────────────
//
// Voicemail nodes (voicemail + listen_voicemail) point at a voicemail
// BOX (config.voicemail_box_id), not a recording folder. This label is
// the equivalent of FolderLinkLabel for boxes: fetches the box, shows
// its name, opens a quick-view dialog on click.

const VOICEMAIL_NODE_TYPES = new Set<string>(['voicemail', 'listen_voicemail']);

/** Returns the linked voicemail-box id for a voicemail-bearing node, or null. */
export function getVoicemailBoxId(node: IvrNodeRow): string | null {
  if (!VOICEMAIL_NODE_TYPES.has(node.node_type)) return null;
  const cfg = (node.config ?? {}) as Record<string, unknown>;
  const id = cfg.voicemail_box_id ?? cfg.box_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

export function VoicemailBoxLabel({
  boxId, nodeType,
}: { boxId: string | null; nodeType: 'voicemail' | 'listen_voicemail' }) {
  const enabled = Boolean(boxId);
  const q = useQuery({
    queryKey: ['voicemail-box', boxId],
    queryFn: () => voicemails.getBox(boxId!),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!boxId) {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-slate-400 italic"
        title={
          nodeType === 'voicemail'
            ? 'No voicemail box linked yet — open the node editor to assign one. Callers reaching this node would hang up.'
            : 'No voicemail box linked yet — open the node editor to assign one. Callers reaching this node would have no inbox to listen to.'
        }
      >
        <Voicemail className="h-3 w-3" aria-hidden />
        no box
      </span>
    );
  }

  if (q.isPending) {
    return (
      <span className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-slate-400">
        <Voicemail className="h-3 w-3 animate-pulse" aria-hidden />
        box…
      </span>
    );
  }

  if (q.isError || !q.data) {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11.5px] text-amber-600"
        title={`Voicemail box ${boxId} could not be loaded — it may have been deleted`}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden />
        box unavailable
      </span>
    );
  }

  return <VoicemailBoxButton box={q.data} nodeType={nodeType} />;
}

function VoicemailBoxButton({
  box, nodeType,
}: {
  box: Awaited<ReturnType<typeof voicemails.getBox>>;
  nodeType: 'voicemail' | 'listen_voicemail';
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="hidden md:inline-flex items-center gap-1 max-w-[180px] text-[11.5px] text-amber-700 hover:text-amber-800 hover:underline focus:outline-none focus:ring-2 focus:ring-amber-400/40 rounded"
        title={`${nodeType === 'voicemail' ? 'Leave message' : 'Listen to'} box: ${box.name}`}
      >
        <Voicemail className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{box.name}</span>
      </button>
      {open && <VoicemailBoxQuickViewDialog box={box} nodeType={nodeType} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Voicemail-box messages popup — overlays the IVR tree without
 * navigation. Same shape as FolderQuickEditDialog: big modal
 * (max-w-3xl) listing the actual messages stored in the box, with
 * inline play, mark-read/unread, and delete. Box-level settings
 * (greeting recording, PIN, notify channels) are not exposed here —
 * link at the bottom jumps to the full voicemails page where the
 * box-level UI lives.
 */
function VoicemailBoxQuickViewDialog({
  box, nodeType, onClose,
}: {
  box: Awaited<ReturnType<typeof voicemails.getBox>>;
  nodeType: 'voicemail' | 'listen_voicemail';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const accessToken = useAuth((s) => s.accessToken);

  const messagesQuery = useQuery({
    queryKey: ['voicemail-messages', box.id],
    queryFn: () => voicemails.listMessages(box.id, { limit: 200 }),
  });
  const messages = messagesQuery.data?.rows ?? [];

  const [openId, setOpenId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection when the message set changes (e.g. after a bulk delete).
  // The set itself is keyed on the rows we're showing now.
  const allSelectedOnPage = messages.length > 0 && messages.every((m) => selected.has(m.id));
  const someSelectedOnPage = messages.some((m) => selected.has(m.id));

  function toggleAll(checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) messages.forEach((m) => next.add(m.id));
      else messages.forEach((m) => next.delete(m.id));
      return next;
    });
  }
  function toggleOne(id: string, checked: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }

  const invalidateMessages = (): void => {
    qc.invalidateQueries({ queryKey: ['voicemail-messages', box.id] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => voicemails.markRead(id),
    onSuccess: invalidateMessages,
  });
  const unmarkRead = useMutation({
    mutationFn: (id: string) => voicemails.markUnread(id),
    onSuccess: invalidateMessages,
  });
  const removeMsg = useMutation({
    mutationFn: (id: string) => voicemails.removeMessage(id),
    onSuccess: () => {
      setOpenId(null);
      invalidateMessages();
    },
  });

  // Bulk operations
  const bulkRead = useMutation({
    mutationFn: () => voicemails.bulkMarkRead(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); invalidateMessages(); },
  });
  const bulkUnread = useMutation({
    mutationFn: () => voicemails.bulkMarkUnread(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); invalidateMessages(); },
  });
  const bulkDel = useMutation({
    mutationFn: () => voicemails.bulkDelete(box.id, Array.from(selected)),
    onSuccess: () => { setSelected(new Set()); setOpenId(null); invalidateMessages(); },
  });

  const fmtDur = (ms: number | null): string => {
    if (!ms) return '—';
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Voicemail className="h-4 w-4 text-amber-600" />
            <span>{box.name}</span>
            <span className="text-[10px] uppercase tracking-wider rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-amber-700">
              {nodeType === 'voicemail' ? 'leave-message node' : 'listen node'}
            </span>
            <span className="text-xs text-muted-foreground font-normal">
              · {messages.length} message{messages.length === 1 ? '' : 's'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Bulk action bar (shown when at least one message is selected) */}
        {messages.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-1 pb-2 -mb-1">
            <Checkbox
              checked={allSelectedOnPage}
              data-state={!allSelectedOnPage && someSelectedOnPage ? 'indeterminate' : undefined}
              onCheckedChange={(v) => toggleAll(Boolean(v))}
              aria-label="Select all messages"
            />
            <span className="text-xs text-muted-foreground">
              {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
            </span>
            {selected.size > 0 && (
              <>
                {/* Bulk Mark read / Mark unread — icon-only buttons. Per-row
                    buttons use the same icons; aria-label + title carry the
                    verb for tooltips and screen readers. */}
                <Button
                  size="sm" variant="outline"
                  onClick={() => bulkRead.mutate()}
                  disabled={bulkRead.isPending}
                  className="h-7 w-7 p-0"
                  aria-label="Mark selected as read"
                  title="Mark selected as read"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => bulkUnread.mutate()}
                  disabled={bulkUnread.isPending}
                  className="h-7 w-7 p-0"
                  aria-label="Mark selected as unread"
                  title="Mark selected as unread"
                >
                  <XCircle className="h-3.5 w-3.5 text-amber-600" />
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    voicemails.downloadZip(box.id, Array.from(selected))
                      .catch(() => undefined);
                  }}
                  className="h-7"
                  title="Download selected messages as a ZIP"
                >
                  <Download className="h-3.5 w-3.5 me-1.5" />
                  Download .zip
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    if (!confirm(`Delete ${selected.size} message${selected.size === 1 ? '' : 's'}?`)) return;
                    bulkDel.mutate();
                  }}
                  disabled={bulkDel.isPending}
                  className="h-7 text-destructive border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5 me-1.5" />
                  Delete
                </Button>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                >
                  Clear
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto -mx-6 px-6 py-2">
          {messagesQuery.isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center italic">
              No messages in this box yet.
              {nodeType === 'voicemail'
                ? ' Callers reaching this node will leave their first message here.'
                : ' Callers reaching the matching record-side node will fill this inbox.'}
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
              {messages.map((m) => {
                const isOpen = openId === m.id;
                const audioOk = typeof m.s3_key === 'string' && m.s3_key.length > 0;
                return (
                  <li key={m.id} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {/* Bulk-select checkbox */}
                      <Checkbox
                        checked={selected.has(m.id)}
                        onCheckedChange={(v) => toggleOne(m.id, Boolean(v))}
                        aria-label="Select message"
                        className="shrink-0"
                      />

                      {/* Play — leftmost state column removed; the
                          envelope status indicator + toggle now lives on
                          the right side as a single combined control. */}
                      {audioOk ? (
                        <button
                          type="button"
                          onClick={() => {
                            setOpenId(isOpen ? null : m.id);
                            // Auto-mark read on first play
                            if (!isOpen && !m.read_at) markRead.mutate(m.id);
                          }}
                          className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                          aria-label={isOpen ? 'Close player' : 'Play message'}
                        >
                          <Play className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className="shrink-0 h-6 w-6 inline-flex items-center justify-center text-[9px] italic text-slate-300" title="No audio file">
                          —
                        </span>
                      )}

                      {/* From + date — collapse to a single column */}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-slate-700 truncate">
                          {m.from_number ?? 'Unknown number'}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>

                      {/* Inline player when open */}
                      {isOpen && audioOk && (
                        <div className="w-72 shrink-0">
                          <AudioPlayer
                            src={voicemails.audioUrl(m.id, accessToken)}
                            compact
                          />
                        </div>
                      )}

                      {/* Duration */}
                      <span className="text-xs text-muted-foreground tabular-nums w-12 text-right shrink-0">
                        {fmtDur(m.duration_ms)}
                      </span>

                      {/* Envelope toggle — single icon that BOTH shows
                          read/unread status AND toggles it on click.
                          Replaces the separate left state-column + right
                          action-button pair. MailOpen (slate) = currently
                          read, Mail (amber) = currently unread. */}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => (m.read_at ? unmarkRead.mutate(m.id) : markRead.mutate(m.id))}
                        disabled={markRead.isPending || unmarkRead.isPending}
                        className="h-7 w-7 p-0 shrink-0"
                        aria-label={m.read_at ? 'Mark unread (currently read)' : 'Mark read (currently unread)'}
                        title={m.read_at ? 'Mark unread' : 'Mark read'}
                      >
                        {m.read_at ? (
                          <MailOpen className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Mail className="h-4 w-4 text-amber-600" />
                        )}
                      </Button>

                      {/* Download single */}
                      {audioOk && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => {
                            voicemails.downloadOne(m.id).catch(() => undefined);
                          }}
                          className="h-7 w-7 p-0 shrink-0 text-slate-400 hover:text-indigo-600"
                          aria-label="Download message"
                          title="Download .wav"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      )}

                      {/* Delete */}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => removeMsg.mutate(m.id)}
                        disabled={removeMsg.isPending}
                        className="h-7 w-7 p-0 shrink-0 text-slate-400 hover:text-red-600"
                        aria-label="Delete message"
                        title="Delete message"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {/* Transcription preview when present */}
                    {m.transcription && (
                      <p className="mt-1 ms-7 text-[11px] text-muted-foreground line-clamp-2 italic">
                        “{m.transcription}”
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground">
            {nodeType === 'voicemail'
              ? 'Callers reaching this node leave a message in this box.'
              : 'Callers reaching this node listen to messages from this box (DTMF inbox playback).'}
          </p>
          <Link
            to={`/voicemails?box=${box.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
          >
            <ExternalLink className="h-3 w-3" />
            Open full inbox (box settings, greeting)
          </Link>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── On-complete label ────────────────────────────────────────────────────────
//
// For non-menu nodes: the row shows a small label describing where the node
// routes when it finishes. See `on_complete` (migration 0034) and the
// dedicated `on_complete_node_id` column (migration 0035).
//
// States, in the order tested:
//   - 'hunt_back' (default): "↩ parent"
//   - 'hangup':              "☎ hangup"
//   - 'custom_node' + valid target: "→ hunts to IVR-<ref>" (clickable)
//   - 'custom_node' + missing/soft-deleted target: "⚠ hunts to missing node"
//     in amber — this is the case when the FK (ON DELETE SET NULL) has
//     already nulled the pointer, or when the row was written without
//     setting a target.

export function OnCompleteLabel({
  node,
  target,
  onJumpToNode,
}: {
  node: IvrNodeRow;
  /** Resolved target node for on_complete='custom_node', or null/undefined. */
  target: IvrNodeRow | null | undefined;
  onJumpToNode?: (nodeId: string) => void;
}) {
  const mode = (node.on_complete ?? 'hunt_back') as string;

  if (mode === 'hunt_back') {
    // Default behavior: stay visually quiet. Hunting back to parent is the
    // implicit default for every node, so showing a label would clutter the
    // list view without adding information. Only render when the node
    // deviates from the default (hangup or custom_node).
    return null;
  }

  if (mode === 'hangup') {
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11px] text-slate-500"
        title="Ends the call when complete"
      >
        <PhoneOff className="h-3 w-3" aria-hidden />
        hangup
      </span>
    );
  }

  // mode === 'custom_node'
  if (!target) {
    // Target missing — either on_complete_node_id is null (never set, or
    // nulled by ON DELETE SET NULL when the target was deleted) or the id
    // points to a row we can't resolve from the current tree snapshot.
    return (
      <span
        className="hidden md:inline-flex items-center gap-1 text-[11px] font-medium text-amber-600"
        title="on_complete is 'custom_node' but no valid target is configured"
      >
        <AlertTriangle className="h-3 w-3" aria-hidden />
        hunts to missing node
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onJumpToNode?.(target.id); }}
      className="hidden md:inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-700 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-400/40 rounded"
      title={`Jumps to ${formatRef(target.ref_number)} — ${target.name}`}
    >
      <ArrowRight className="h-3 w-3" aria-hidden />
      hunts to {formatRef(target.ref_number)}
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface NodeRowProps {
  node: IvrNodeRow;
  edge: IvrEdgeRow | null;
  outgoingEdges: ReadonlyArray<IvrEdgeRow>;
  depth: number;
  isRoot: boolean;
  isLastChild: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  canMutate: boolean;
  /**
   * Resolved target node for on_complete='custom_node', or null if the
   * target id is unset / points to a node that isn't in the current
   * tree snapshot. Used only when `node.on_complete === 'custom_node'`.
   */
  onCompleteTarget?: IvrNodeRow | null;
  onToggle?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /**
   * Fired when the user clicks the [+] plus button. The handler receives
   * the anchor DOMRect so the popover can position itself.
   * NB: Only rendered on menu-like parent types (menu /
   * playback_settings). Non-menu nodes use `on_complete` instead to
   * declare their successor — see OnCompleteLabel above.
   */
  onAddChild?: (anchor: DOMRect) => void;
  /** Scroll + highlight the given node in the same list view. */
  onJumpToNode?: (nodeId: string) => void;
}

export const NodeRow = forwardRef<HTMLDivElement, NodeRowProps>(function NodeRow(
  {
    node, edge, outgoingEdges, depth, isRoot, isLastChild, hasChildren,
    isExpanded, canMutate, onCompleteTarget,
    onToggle, onEdit, onDelete, onAddChild, onJumpToNode,
  },
  ref,
) {
  const style = styleForType(node.node_type);
  const summary = summariseNode(node, outgoingEdges);
  const branchKey = edge?.branch_key ?? null;
  const isOrphan = !edge && !isRoot;

  // Menus branch by DTMF edge — they keep the [+] picker and don't show
  // the on_complete label. Non-menu nodes go the other way: no [+], and
  // a visible label describing where they route on completion.
  const menuLike = isMenuLike(node.node_type);

  return (
    <div
      ref={ref}
      role="treeitem"
      aria-level={depth + 1}
      aria-expanded={hasChildren ? isExpanded : undefined}
      className={cn(
        'group relative flex items-stretch text-sm select-none transition-colors',
        !node.active && 'opacity-50',
      )}
    >
      {/* Indentation rails */}
      {depth > 0 && (
        <div className="flex shrink-0" aria-hidden>
          {Array.from({ length: depth }).map((_, i) => (
            <span
              key={i}
              className={cn(
                'w-6 relative',
                // every depth level draws a faint vertical
                'border-r border-slate-100',
                // but the last child hides the vertical below its connector
                i === depth - 1 && isLastChild && 'border-r-transparent',
              )}
            >
              {/* Horizontal connector on the final level */}
              {i === depth - 1 && (
                <span
                  className="absolute left-0 top-1/2 h-px w-4 bg-slate-200"
                  aria-hidden
                />
              )}
              {/* Elbow — top half vertical only, to give L-shape */}
              {i === depth - 1 && isLastChild && (
                <span
                  className="absolute left-0 top-0 h-1/2 w-px bg-slate-200"
                  aria-hidden
                />
              )}
            </span>
          ))}
        </div>
      )}

      {/* Row body */}
      <div
        className={cn(
          'flex-1 min-w-0 flex items-center gap-2.5 px-2.5 py-1.5',
          'border-b border-slate-100',
          'hover:bg-indigo-50/40 cursor-pointer',
          isRoot && 'bg-slate-50/50',
        )}
        onClick={() => onEdit?.()}
      >
        {/* Chevron */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle?.(); }}
          className={cn(
            'shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400',
            hasChildren
              ? 'hover:bg-slate-200/60 hover:text-slate-700'
              : 'invisible',
          )}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-150',
              isExpanded && 'rotate-90',
            )}
          />
        </button>

        {/* Branch key / ROOT */}
        <BranchKeyPill k={branchKey} isRoot={isRoot} isOrphan={isOrphan} />

        {/* Node card */}
        <div className={cn(
          'shrink-0 inline-flex items-stretch overflow-hidden rounded-md border bg-white',
          style.border,
        )}>
          <span className={cn('w-1', style.rail)} aria-hidden />
          <div className="flex items-center gap-2 px-2.5 py-1">
            <span className="font-mono text-[10.5px] text-slate-400">
              {formatRef(node.ref_number)}
            </span>
            <span className="text-slate-300" aria-hidden>\u00b7</span>
            <NodeTypeBadge type={node.node_type} />
            {node.name && (
              <>
                <span className="text-slate-300">:</span>
                <span className="text-slate-700 truncate max-w-[220px]">{node.name}</span>
              </>
            )}
          </div>
        </div>

        {/* Folder / voicemail-box link — clickable label for resource-bound
            node types. Wins over the plain `summary` for those types
            (summariseNode returns null for them). */}
        {RECORDING_FOLDER_NODE_TYPES.has(node.node_type) ? (
          <FolderLinkLabel folderId={getRecordingFolderId(node)} />
        ) : VOICEMAIL_NODE_TYPES.has(node.node_type) ? (
          <VoicemailBoxLabel
            boxId={getVoicemailBoxId(node)}
            nodeType={node.node_type as 'voicemail' | 'listen_voicemail'}
          />
        ) : (
          summary && (
            <span className="hidden md:inline text-[11px] text-slate-500 truncate max-w-[160px]">
              {summary}
            </span>
          )
        )}

        {/* On-complete route label — non-menu nodes only. Menus branch by
            DTMF edge so the label isn't meaningful for them (and they'd
            never display it anyway since their `on_complete` is
            effectively ignored by the interpreter as long as at least one
            outgoing edge matches). */}
        {!menuLike && (
          <OnCompleteLabel
            node={node}
            target={onCompleteTarget}
            onJumpToNode={onJumpToNode}
          />
        )}

        {/* Recording slot chips — all 4 slots, filled=clickable, empty=muted */}
        <RecordingSlotChips node={node} />

        {/* Edge label */}
        {edge?.label && (
          <span className="hidden xl:inline text-[11px] text-slate-400 italic truncate max-w-[100px]">
            &ldquo;{edge.label}&rdquo;
          </span>
        )}

        {/* Actions — revealed on hover */}
        <div className={cn(
          'ml-auto shrink-0 flex items-center gap-0.5',
          'opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity',
        )}>
          {/* [+] add-child only on menu-like parents. Non-menu nodes
              declare their successor via the `on_complete` policy on
              the node itself, not via a tree edge, so exposing a [+]
              would be misleading. */}
          {canMutate && menuLike && onAddChild && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                onAddChild(rect);
              }}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-indigo-500 hover:bg-indigo-100"
              aria-label={`Add child to ${node.name}`}
              title="Add child"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-500 hover:bg-slate-100"
            aria-label={`Edit ${node.name}`}
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {canMutate && onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="h-6 w-6 inline-flex items-center justify-center rounded text-red-500 hover:bg-red-50"
              aria-label={`Delete ${node.name}`}
              title="Delete"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ── Recording slot chips ──────────────────────────────────────────────────────
//
// Shows all 4 recording slots (Main / Intro 1 / Intro 2 / Outro) on the same
// row as the node, before the hover-action buttons. Filled slots are colored
// and clickable (toggles an inline AudioPlayer). Empty slots are muted/gray
// and not clickable. TTS-prompt-key slots count as "filled" visually but are
// NOT clickable since there is no audio file to stream.

type SlotKey = 'main' | 'intro1' | 'intro2' | 'outro';

interface SlotDef {
  key: SlotKey;
  shortLabel: string;   // fits in the row chip: "M" / "I1" / "I2" / "O"
  fullLabel: string;    // tooltip text: "Main" / "Intro 1" / "Intro 2" / "Outro"
  recordingId: string | null;  // null = empty; non-null = has audio file
  ttsOnly: boolean;            // has a prompt key but no recording id
  skippable?: boolean;         // intro1/intro2/outro only — false = caller can't interrupt
}

function buildSlots(node: IvrNodeRow): SlotDef[] {
  const cfg = (node.config ?? {}) as Record<string, unknown>;
  return [
    {
      key: 'main',
      shortLabel: 'M',
      fullLabel: 'Main',
      recordingId: node.main_recording_id,
      ttsOnly: !node.main_recording_id && Boolean(cfg.main_prompt_key),
    },
    {
      key: 'intro1',
      shortLabel: 'I1',
      fullLabel: 'Intro 1',
      recordingId: node.intro1_recording_id,
      ttsOnly: false,
      skippable: node.intro1_skippable,
    },
    {
      key: 'intro2',
      shortLabel: 'I2',
      fullLabel: 'Intro 2',
      recordingId: node.intro2_recording_id,
      ttsOnly: false,
      skippable: node.intro2_skippable,
    },
    {
      key: 'outro',
      shortLabel: 'O',
      fullLabel: 'Outro',
      recordingId: node.outro_recording_id,
      ttsOnly: !node.outro_recording_id && Boolean(cfg.outro_prompt_key),
      skippable: node.outro_skippable,
    },
  ];
}

export function RecordingSlotChips({ node }: { node: IvrNodeRow }) {
  const accessToken = useAuth((s) => s.accessToken);
  const [openSlot, setOpenSlot] = useState<SlotKey | null>(null);

  const slots = buildSlots(node);

  const activeSlot = openSlot ? slots.find((s) => s.key === openSlot) : null;
  const showPlayer = Boolean(activeSlot?.recordingId);

  return (
    <div
      className="hidden lg:flex flex-row items-center shrink-0 gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Inline audio player — shown to the LEFT of the chips when a slot is open.
          `compact` keeps the player short enough that opening it does NOT
          increase the row height beyond the chip-only baseline. */}
      {showPlayer && activeSlot?.recordingId && (
        <div className="w-52">
          <AudioPlayer
            src={recordings.audioUrl(activeSlot.recordingId, accessToken)}
            compact
          />
        </div>
      )}

      <div className="flex items-center gap-1">
        {slots.map((slot) => {
          const filled = Boolean(slot.recordingId) || slot.ttsOnly;
          const clickable = Boolean(slot.recordingId); // ttsOnly = filled but not clickable
          const isOpen = openSlot === slot.key;
          const nonSkip = slot.skippable === false;

          if (!filled) {
            // Empty slot — muted, not clickable
            return (
              <span
                key={slot.key}
                title={slot.fullLabel}
                className="inline-flex items-center justify-center rounded border border-slate-100 bg-slate-50 text-slate-300 cursor-default text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[20px]"
              >
                {slot.shortLabel}
              </span>
            );
          }

          if (!clickable) {
            // TTS-only slot — colored but not clickable (no audio file)
            return (
              <span
                key={slot.key}
                title={`${slot.fullLabel} (TTS prompt)${nonSkip ? ' — non-skippable (caller can’t interrupt)' : ''}`}
                className={cn(
                  'inline-flex items-center justify-center gap-0.5 rounded border cursor-default text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[20px]',
                  nonSkip
                    ? 'border-amber-300 bg-amber-50 text-amber-600'
                    : 'border-indigo-200 bg-indigo-50 text-indigo-400',
                )}
              >
                {slot.shortLabel}
                {nonSkip && <Lock className="h-2 w-2 shrink-0" aria-hidden />}
              </span>
            );
          }

          // Filled + clickable recording slot
          return (
            <button
              key={slot.key}
              type="button"
              title={`${isOpen ? 'Hide' : 'Play'} ${slot.fullLabel}${nonSkip ? ' — non-skippable (caller can’t interrupt)' : ''}`}
              aria-label={`${isOpen ? 'Hide' : 'Play'} ${slot.fullLabel} recording${nonSkip ? ', non-skippable' : ''}`}
              aria-expanded={isOpen}
              onClick={() => setOpenSlot(isOpen ? null : slot.key)}
              className={cn(
                'inline-flex items-center justify-center gap-0.5 rounded border text-[10px] font-semibold leading-none px-1.5 py-0.5 min-w-[20px]',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40 transition-colors',
                isOpen
                  ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700'
                  : nonSkip
                    ? 'bg-amber-50 text-amber-700 border border-amber-300 cursor-pointer hover:bg-amber-100'
                    : 'bg-indigo-50 text-indigo-600 border border-indigo-200 cursor-pointer hover:bg-indigo-100',
              )}
            >
              {slot.shortLabel}
              {nonSkip
                ? <Lock className="h-2 w-2 shrink-0" aria-hidden />
                : <Play className="h-2 w-2 shrink-0" aria-hidden />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
